<template>
  <!-- The building polygon layer showing in mapview when the level data is missing -->
  <GeojsonLayer
    v-if="layerLevelBuildingSource"
    :before="before"
    :layer="layerLevelBuildingStyle"
    :source="layerLevelBuildingSource"
    :sourceId="LAYER_LEVEL_BUILDING_SOURCE_ID"
    @layerMouseEnter="handleMouseEnter"
    @layerMouseLeave="handleMouseLeave"
    @layerMouseMove="handleMouseMove"
  />
  <GeojsonLayer
    :before="before"
    :layer="layer"
    :layerId="LAYER_ID"
    :source="source"
    :sourceId="SOURCE_ID"
    @layerClicked="handleLayerClicked"
    @layerMouseEnter="handleMouseEnter"
    @layerMouseLeave="handleMouseLeave"
    @layerMouseMove="handleMouseMove"
  />
  <div v-show="false">
    <MapPopup ref="instanceRef" v-bind="instanceProps" />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import GeojsonLayer from '@/components/map/mapbox-layers/GeojsonLayer.vue'
import {
  FillExtrusionLayerSpecification,
  GeoJSONSourceSpecification,
  LngLatLike,
  MapMouseEvent,
  Popup
} from 'mapbox-gl'
import useMapbox from '@/composables/useMapbox'
import { useStore } from 'vuex'
import { ComponentProps } from '@/utils/ComponentProps'
import MapPopup from '@/components/map/popups/MapPopup.vue'
import { COCLASSES, COCLASSGROUPS } from '@/interfaces/coclass.interface'
import { useI18n } from 'vue-i18n'
import {
  createColorCircleLabel,
  createDivider,
  createSmallIconSubtitle,
  createSmallSubtitle,
  createSmallTitle
} from '@/components/Builder'
import useColoredEntities from '@/composables/useColoredEntities'
import { getColorForValue } from '@/utils/colorScale'
import { storeToRefs } from 'pinia'
import { useLoadedEntities } from '@/pinia/loadedEntities'
import { useSelectionStore } from '@/pinia/selectionNew'

const LAYER_LEVEL_BUILDING_ID = 'layer-building-rooms-building'
const LAYER_LEVEL_BUILDING_SOURCE_ID = 'sourceBuildingRoomsBuilding'
const LAYER_ID = 'building-rooms'
const SOURCE_ID = 'building-rooms-source'
const DEFAULT_COLOR = '#1d234f'

type Props = {
  before: string
  opacity: number
}

defineProps<Props>()
const instanceProps = ref<ComponentProps<typeof MapPopup>>({})

const { locale, t } = useI18n()
const store = useStore()
const selectedBuildingId = computed<string | null>(
  () => store.getters['mapview/selectedBuildingId'] ?? null
)
const clickedItemType = computed(() => store.state.clickedItemType)
const features = ref<GeoJSON.Feature[]>([])
const hoveredRoomId = ref<string>()
const coloredEntities = useColoredEntities()
const { loadedEntities, isLoading } = storeToRefs(useLoadedEntities())
const spaces = computed(() => loadedEntities.value.filter((e) => e.type === 'SPACE'))
const data = computed<GeoJSON.FeatureCollection>(() => ({
  type: 'FeatureCollection',
  features: features.value
}))
const popup = new Popup({
  closeButton: false,
  closeOnClick: false,
  className: 'colored-spaces-popup',
  offset: 20
})
const instanceRef = ref<InstanceType<typeof MapPopup> | null>(null)
const hoveredRoomValue = computed(
  () => coloredEntities.data?.find((v) => v.id === hoveredRoomId.value)?.value
)

const color = computed(() =>
  getColorForValue(coloredEntities.colorScale, hoveredRoomValue.value ?? 0)
)

const colorLabel = computed<string | undefined>(() =>
  hoveredRoomValue.value !== undefined
    ? coloredEntities.title +
      ': ' +
      coloredEntities.formatter(hoveredRoomValue.value) +
      coloredEntities.unit
    : undefined
)
const isMapviewShowing = computed(() => store.getters.isMapviewShowing)
const selection = useSelectionStore()
const { map } = useMapbox()

const isVisible = computed(() => {
  return (
    (clickedItemType.value === 'building' || clickedItemType.value === 'room') &&
    selectedBuildingId.value !== null
  )
})

const layerLevelBuildingSource = computed<GeoJSONSourceSpecification>(
  (): GeoJSONSourceSpecification => {
    return {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: loadedEntities.value
          .filter((e) => e.type === 'CONSTRUCTION' && selection.selectedEntityIds.includes(e.id))
          .map((e) => ({
            type: 'Feature',
            properties: e.properties,
            geometry: e.geometry
          }))
      }
    }
  }
)

const layerLevelBuildingStyle = computed<FillExtrusionLayerSpecification>(() => {
  return {
    id: LAYER_LEVEL_BUILDING_ID,
    source: LAYER_LEVEL_BUILDING_SOURCE_ID,
    type: 'fill-extrusion',
    layout: {
      visibility:
        isMapviewShowing.value && (!spaces.value.some((s) => !!s.geometry) || isLoading.value)
          ? 'visible'
          : 'none'
    },
    paint: {
      'fill-extrusion-color': '#1d234f',
      'fill-extrusion-height': ['get', 'height'],
      'fill-extrusion-base': 0,
      'fill-extrusion-opacity': 0.9,
      'fill-extrusion-vertical-gradient': false
    }
  }
})

const layer = computed<FillExtrusionLayerSpecification>(() => ({
  id: LAYER_ID,
  type: 'fill-extrusion',
  layout: {
    visibility: isVisible.value ? 'visible' : 'none'
  },
  source: SOURCE_ID,
  paint: {
    'fill-extrusion-color': ['coalesce', ['feature-state', 'color'], DEFAULT_COLOR],
    'fill-extrusion-height': ['get', 'height'],
    'fill-extrusion-base': ['get', 'base'],
    'fill-extrusion-opacity': 0.9
  }
}))

onMounted(() => {
  map?.on('mousemove', LAYER_ID, (e: MapMouseEvent) => updatePopup(e))
  map?.on('mousemove', LAYER_LEVEL_BUILDING_ID, (e: MapMouseEvent) => updatePopup(e))
  map?.on('mouseleave', LAYER_ID, () => removePopup())
  map?.on('mouseleave', LAYER_LEVEL_BUILDING_ID, () => removePopup())
})

watch(
  spaces,
  async () => {
    if (!spaces.value) return
    const lowestLevel = spaces.value.reduce(
      (acc, space) =>
        parseInt(space.parent?.properties?.level ?? '1') < acc
          ? parseInt(space.parent?.properties?.level ?? '1')
          : acc,
      10000
    )
    features.value = spaces.value.map((space, i) => {
      const leveli = parseInt(space.parent?.properties?.level ?? '1') - lowestLevel
      const height = 4
      const explode = 1
      const base = leveli * (height + explode)
      const top = base + height
      return {
        type: 'Feature',
        properties: {
          height: top,
          base,
          color: `rgb(${(i * 70) % 255}, ${(i * 50) % 255}, ${(i * 30) % 255})`,
          ...space.properties
        } as GeoJSON.GeoJsonProperties,
        geometry: space.geometry as GeoJSON.Polygon
      }
    })
  },
  { immediate: true }
)

const source = computed<GeoJSONSourceSpecification>(() => ({
  type: 'geojson',
  promoteId: 'id',
  data: data.value
}))

const handleMouseEnter = () => {}

const handleMouseLeave = () => {
  hoveredRoomId.value = undefined
}

const handleMouseMove = (e: MapMouseEvent) => {
  const features = map?.queryRenderedFeatures(e.point)
  const feature = features?.[0]
  if (!feature) {
    popup.remove()
  }
  const id = feature?.properties?.id as string | undefined
  hoveredRoomId.value = id
  if (feature && feature.properties?.type === 'CONSTRUCTION') {
    updateMissingSpaces(feature)
  }
}

const handleLayerClicked = (e: MapMouseEvent) => {
  if (!e.features?.length) return
  const feature = e.features[0]

  store.commit('SET_CURRENT_ROOM', { ...feature })
  store.commit('SET_CLICKED_ITEM_TYPE', { type: 'room' })
}

watch(hoveredRoomId, (newId) => {
  if (newId) {
    const spaceFeature = features.value.find((f) => f.properties?.id === newId)
    if (spaceFeature) {
      updateInstanceProps(spaceFeature)
    }
  }
})

function updateMissingSpaces(construction: GeoJSON.Feature<GeoJSON.Geometry> | undefined) {
  if (!construction) return

  instanceProps.value = {
    rows: [
      createSmallTitle(construction.properties?.displayName),
      createSmallSubtitle(
        isLoading.value
          ? t('general__text__loading') + '...'
          : t('general__data__data-missing-space-geo'),
        {
          style: { 'margin-bottom': '8px' }
        }
      )
    ]
  }
}

function updateInstanceProps(space: GeoJSON.Feature<GeoJSON.Geometry> | undefined) {
  if (!space) return

  let classCode: string | undefined = undefined

  if (space.properties?.classification) {
    const classification = space.properties.classification
    if (classification.USED && classification.USED.classCode) {
      classCode = classification.USED.classCode
    } else if (classification.PLANNED && classification.PLANNED.classCode) {
      classCode = classification.PLANNED.classCode
    }
  }

  const coclass = COCLASSES.find((c) => c.coclass === classCode)
  const roomGroup = coclass ? COCLASSGROUPS[coclass.roomGroup][locale.value] : ' - '
  instanceProps.value = {
    rows: [
      createSmallTitle(space.properties?.displayName),
      createSmallSubtitle(space.properties?.aliasId, { style: { 'margin-bottom': '8px' } }),
      createSmallIconSubtitle(roomGroup, 'IconTag'),
      ...(space.properties?.capacity
        ? [
            createSmallIconSubtitle(space.properties.capacity.toString() ?? '', 'IconUsers', {
              'margin-top': '4px'
            })
          ]
        : []),
      ...(colorLabel.value
        ? [createDivider(), createColorCircleLabel(color.value.toHex(), colorLabel.value)]
        : [])
    ]
  }
}

function updatePopup(e: MapMouseEvent): void {
  if (!features.value || !map) return
  map.getCanvas().style.cursor = 'pointer'
  const properties = e.features?.at(0)?.properties
  if (!properties) return
  const lngLat: LngLatLike = e.lngLat
  if (!instanceRef.value) return
  const html = instanceRef.value.$el
  popup.setDOMContent(html).setLngLat(lngLat).addTo(map)
  popup.removeClassName('hidden')
}
function removePopup(): void {
  if (map) map.getCanvas().style.cursor = ''
  popup.remove()
}
</script>
